<template>
  <div class="nx-layout-default-toolbar">
    <v-navigation-drawer
      id="app-nav-drawer"
      v-model="drawer"
      :clipped="$vuetify.breakpoint.lgAndUp"
      app
      temporary
    >
      <v-list
        v-if="drawer && navigation.length > 0"
        dense
      >
        <!--  group with children -->
        <template v-for="(item, index) in navigation">
          <v-list-group
            v-if="item.items && item.items.length > 0"
            :key="`${index}-${item.title}`"
            :group="item.group"
            :prepend-icon="item.icon"
            no-action
          >
            <v-list-tile
              slot="activator"
              ripple
            >
              <v-list-tile-content>
                <v-list-tile-title>
                  {{ $t(`message.navigation.${item.title}.title`) }}
                </v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>

            <template
              v-for="(child, i) in forCurrentSite(item.items)"
            >
              <v-list-tile
                v-if="!child.group"
                :key="i"
                :to="generateTo(child.route)"
                :href="child.url"
                :target="generateTarget(child)"
                active-class
                ripple
              >
                <v-list-tile-content>
                  <v-list-tile-title>
                    <span>{{
                      $te(`message.navigation.${item.title}.${child.title}`)
                        ? $t(`message.navigation.${item.title}.${child.title}`)
                        : child.title
                    }}</span>
                  </v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-list-group
                v-else
                :key="i"
                no-action
                sub-group
              >
                <v-list-tile
                  slot="activator"
                  ripple
                >
                  <v-list-tile-content>
                    <v-list-tile-title>
                      {{ $t(`message.navigation.${item.title}.${child.title}.title`) }}
                    </v-list-tile-title>
                  </v-list-tile-content>
                </v-list-tile>

                <v-list-tile
                  v-for="(grandchild, j) in child.items"
                  :key="`${i}-${j}`"
                  :to="generateTo(grandchild.router)"
                  :href="grandchild.url"
                  :target="generateTarget(grandchild)"
                  ripple
                >
                  <v-list-tile-content>
                    <v-list-tile-title>
                      <span>{{
                        $te(`message.navigation.${item.title}.${child.title}.${grandchild.title}`)
                          ? $t(`message.navigation.${item.title}.${child.title}.${grandchild.title}`)
                          : grandchild.title
                      }}</span>
                    </v-list-tile-title>
                  </v-list-tile-content>
                </v-list-tile>
              </v-list-group>
            </template>
          </v-list-group>

          <v-list-tile
            v-else
            :key="`${index}-${item.title}`"
            :prepend-icon="item.icon"
            no-action
            :to="generateTo(item.route)"
            :href="item.url"
            :target="generateTarget(item)"
            ripple
          >
            <v-list-tile-avatar
              v-if="item.icon"
              size="24"
            >
              <v-icon>{{ item.icon }}</v-icon>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title>
                {{ $t(`message.navigation.${item.title}.title`) }}
              </v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
        </template>
      </v-list>
    </v-navigation-drawer>

    <v-navigation-drawer
      v-if="features.STAFF_NOTIFICATIONS"
      v-model="notifications"
      fixed
      lazy
      temporary
      right
      hide-overlay
      width="328"
      class="nx-ignore-loading nx-primary--darken-3"
    >
      <nx-staff-notification-drawer :active="notifications" />
    </v-navigation-drawer>

    <v-navigation-drawer
      v-if="features.CONTENT_BAG_FEATURE"
      v-model="bag"
      fixed
      disable-resize-watcher
      app
      right
      class="nx-ignore-loading"
      width="360"
    >
      <nx-bag class="pt-5" />
    </v-navigation-drawer>

    <v-toolbar
      id="app-nav"
      :clipped-left="$vuetify.breakpoint.lgAndUp"
      clipped-right
      app
      color="nx-primary--darken-3"
      dark
      dense
      fixed
      flat
    >
      <v-toolbar-side-icon
        :disabled="disabled"
        class="ml-0"
        @click.stop="drawer = !drawer"
      />
      <v-toolbar-title
        :class="{
          'ml-0': $vuetify.breakpoint.xsOnly,
          'ml-1': $vuetify.breakpoint.smAndUp,
          'toolbar-title': true,
        }"
      >
        <nx-layout-default-logo />
      </v-toolbar-title>

      <nx-default-toolbar-before-spacer-items />

      <v-spacer />

      <nx-default-toolbar-after-spacer-items />

      <v-toolbar-items>
        <nx-toolbar-site-selector
          :allowed-sites="allowedSites"
          :current-site-id="currentSiteId"
          @input="navigateToSite"
        />

        <v-btn
          v-if="features.DASHBOARD_FEATURE"
          icon
          :to="{ 'name': 'dashboard' }"
          :title="$t('message.homepage.dashboard')"
        >
          <v-icon>space_dashboard</v-icon>
        </v-btn>

        <nx-file-upload-list class="hidden-xs-only" />

        <v-btn
          icon
          :to="{ name: 'searcher', params: { entity: 'content' } }"
          :title="$t('message.common.search')"
        >
          <v-icon>search</v-icon>
        </v-btn>

        <v-btn
          v-if="features.CONTENT_BAG_FEATURE"
          icon
          class="hidden-xs-only"
          :title="$t('message.menu.bag')"
          :color="bag ? 'accent' : null"
          @click.stop="toggleBag"
        >
          <v-icon>inbox</v-icon>
        </v-btn>

        <v-btn
          v-if="features.STAFF_NOTIFICATIONS"
          class="hidden-xs-only"
          icon
          :title="$t('message.menu.notifications')"
          :color="notifications ? 'accent' : null"
          @click.stop="toggleNotifications"
        >
          <svg
            v-if="hasUnreadNotifications"
            class="unread-notifications"
            :class="notifications ? 'accent' : null"
            xmlns="http://www.w3.org/2000/svg"
            width="6"
            height="6"
            viewBox="0 0 6 6"
            fill="none"
          >
            <path
              d="M6 3C6 4.65685 4.65685 6 3 6C1.34315 6 0 4.65685 0 3C0 1.34315 1.34315 0 3 0C4.65685 0 6 1.34315 6 3Z"
              fill="#F06657"
            />
          </svg>
          <v-icon>notifications</v-icon>
        </v-btn>

        <nx-locale-selector-menu />
        <v-menu
          offset-y
          left
        >
          <v-btn
            slot="activator"
            :title="$t('message.menu.myprofile')"
            icon
            class="hidden-xs-only"
          >
            <v-avatar
              v-if="loggedStaff.avatarUrl"
              size="36"
            >
              <img
                class="nx-avatar"
                :src="loggedStaff.avatarUrl"
                :alt="loggedStaff.nick"
              >
            </v-avatar>
            <v-icon
              v-else
              left
            >
              account_circle
            </v-icon>
          </v-btn>

          <v-list>
            <v-list-tile class="mb-1">
              <v-list-tile-avatar v-if="loggedStaff.avatarUrl">
                <img
                  class="nx-avatar grey"
                  :src="loggedStaff.avatarUrl"
                  :alt="loggedStaff.nick"
                >
              </v-list-tile-avatar>
              <v-icon
                v-else
                left
              >
                account_circle
              </v-icon>
              <v-list-tile-content>
                <v-list-tile-title>{{ loggedStaff.nick }}</v-list-tile-title>
                <v-list-tile-sub-title>{{ loggedStaff.email }}</v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-divider />
            <v-list-tile :to="{ 'name': 'profile' }">
              <v-list-tile-title><span>{{ $t('message.menu.myprofile') }}</span></v-list-tile-title>
            </v-list-tile>
            <v-list-tile @click="resetOnboarding">
              <v-list-tile-title><span>{{ $t('message.menu.viewOnboarding') }}</span></v-list-tile-title>
            </v-list-tile>
            <v-divider />
            <v-list-tile @click="logout">
              <v-list-tile-title><span>{{ $t('message.menu.logout') }}</span></v-list-tile-title>
            </v-list-tile>
          </v-list>
        </v-menu>
      </v-toolbar-items>
    </v-toolbar>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { generateRoute } from '@/router';
import { isEmpty } from 'lodash';
import NxBag from '@/modules/bag/Bag';
import NxToolbarSiteSelector from '@/layouts/components/ToolbarSiteSelector';
import NxLayoutDefaultLogo from '@/layouts/components/Logo';
import NxLocaleSelectorMenu from '@/modules/locale/components/LocaleSelectorMenu.vue';
import customToolbarComponents from '../config';
import NxFileUploadList from '@/modules/upload/FileUploadList.vue';
import NxStaffNotificationDrawer from '@/modules/staff-notification/components/StaffNotificationDrawer.vue';

export default {
  name: 'NxLayoutDefaultToolbar',
  components: {
    NxStaffNotificationDrawer,
    NxFileUploadList,
    NxLocaleSelectorMenu,
    NxToolbarSiteSelector,
    NxLayoutDefaultLogo,
    NxBag,
    NxDefaultToolbarBeforeSpacerItems: customToolbarComponents.beforeSpacerComponent,
    NxDefaultToolbarAfterSpacerItems: customToolbarComponents.afterSpacerComponent,
  },
  props: {
    disabled: Boolean,
  },
  computed: {
    ...mapGetters(['loggedStaff', 'navigation', 'allowedSites', 'features']),
    ...mapGetters('staffNotification', ['unreadNotifications']),
    ...mapGetters('upload', ['hasFiles']),
    ...mapState('upload', ['files']),
    bag: {
      get() {
        return this.$store.state.app.bag;
      },
      set(value) {
        this.$store.dispatch('TOGGLE_BAG', value);
      },
    },
    drawer: {
      get() {
        return this.$store.state.app.drawer;
      },
      set(value) {
        this.$store.dispatch('TOGGLE_DRAWER', value);
      },
    },
    notifications: {
      get() {
        return this.$store.state.app.notifications;
      },
      set(value) {
        this.$store.dispatch('TOGGLE_NOTIFICATIONS', value);
        if (value) {
          this.$store.dispatch('staffNotification/DISMISS_JUST_RECEIVED_NOTIFICATIONS');
        }
      },
    },
    hasUnreadNotifications() {
      return this.unreadNotifications.length > 0;
    },
    currentSiteId() {
      const currentSite = this.allowedSites.find(site => this.$route.params.site === site.idAlias);
      if (currentSite) {
        return currentSite.id;
      }
      return false;
    },
  },
  methods: {
    generateTo(route) {
      if (!route) {
        return undefined;
      }
      return generateRoute(route);
    },
    generateTarget({ url }) {
      if (!url) {
        return undefined;
      }
      return '_blank';
    },
    navigateToSite(site) {
      if (!site) {
        return;
      }
      this.$router.push({
        name: 'default',
        params: { site: site.idAlias },
      });
    },
    logout() {
      this.$store.dispatch('authentication/LOGOUT_STAFF');
    },
    resetOnboarding() {
      this.$store.dispatch('onboarding/SHOW_ALL_ONBOARDING_FEATURES');
    },
    forCurrentSite(items) {
      if (isEmpty(items) || !Array.isArray(items)) {
        return [];
      }
      return items.filter(
        child => !child.onlyForSiteId
          || (child.onlyForSiteId && child.onlyForSiteId === this.currentSiteId),
      );
    },
    toggleNotifications() {
      this.notifications = !this.notifications;
      if (this.bag) {
        this.bag = false;
      }
    },
    toggleBag() {
      this.bag = !this.bag;
      if (this.notifications) {
        this.notifications = false;
      }
    },
  },
};
</script>

<style lang="stylus">
@import '../../assets/stylus/variables.styl';

.nx-layout-default-toolbar {
  #app-nav {
    z-index: 10
  }

  .toolbar-title {
    flex: 0 0 auto;
  }

  #app-nav-drawer {
    z-index: 12
  }

  img.nx-avatar {
    object-fit: cover;
  }

  .v-toolbar__content {
    @media $md-and-up {
      padding: 0 24px;
    }
    @media $xs-only {
      padding: 0 8px;
    }
  }

  .v-btn .v-btn__content .v-icon {
    color: $embedButton_fgColor;
  }

  .v-text-field > .v-input__control {
    height: 100%;
  }

  .media-select-list {
    max-height: 100vh;
    overflow: auto;
  }

  .unread-notifications {
    fill: $nx-error-300;
    position: absolute;
    top: 1px;
    padding: 2px;
    right: 8px;
    background-color: $nx-primary--darken-3;
    width: 10px;
    height: 10px;
    border-radius: 6px;
  }
}
</style>
